export type SortDir = "ASC" | "DESC";
export type Sort = { field: string; dir: SortDir };

export type Page<T> = {
    items: T[];
    totalElements: number;
    totalPages: number;
    page: number;
    size: number;
};

export type SpringPage<T> = {
    content?: T[];
    page?: {
        number?: number;
        size?: number;
        totalElements?: number;
        totalPages?: number;
    };
};

type NormalizeDefaults = {
    page?: number;   // default 0
    size?: number;   // default 10
};

const DFLT: Required<NormalizeDefaults> = {page: 0, size: 10};

function isNumber(n: unknown): n is number {
    return typeof n === "number" && !Number.isNaN(n);
}

function clampNonNegative(n: unknown, fallback: number) {
    return isNumber(n) && n >= 0 ? n : fallback;
}

function coerceItems<T>(arr: unknown): T[] {
    return Array.isArray(arr) ? (arr as T[]) : [];
}

export function normalizePage<T = any>(
    data: SpringPage<T> | undefined | null,
    defaults: NormalizeDefaults = DFLT
): Page<T> {
    const defPage = clampNonNegative(defaults.page, DFLT.page);
    const defSize = clampNonNegative(defaults.size, DFLT.size);

    if (!data || typeof data !== "object") {
        return {items: [], page: defPage, size: defSize, totalPages: 0, totalElements: 0};
    }

    // An endpoint that answers with a bare array is unpaged, so it is one full page.
    // Arrays reach here because typeof [] is "object", and they carry neither key below.
    const raw: unknown = data;
    if (Array.isArray(raw)) {
        const items = coerceItems<T>(raw);
        return {
            items,
            page: 0,
            size: items.length || defSize,
            totalPages: items.length ? 1 : 0,
            totalElements: items.length,
        };
    }

    if ("content" in data || "page" in data) {
        const pg = (data as SpringPage<T>).page ?? {};
        const items = coerceItems<T>((data as SpringPage<T>).content);
        const page = clampNonNegative(pg.number, defPage);
        const size = clampNonNegative(pg.size, defSize);
        const totalPages = clampNonNegative(pg.totalPages, 0);
        const totalElements = clampNonNegative(pg.totalElements, 0);
        return {items, page, size, totalPages, totalElements};
    }

    // An object of some unrecognised shape. Returning an empty page keeps the
    // contract, where falling off the end returned undefined and every caller
    // reads .items straight after the call.
    return {items: [], page: defPage, size: defSize, totalPages: 0, totalElements: 0};
}